import React from "react";
import { ProductCardProps, isValidProductHit, ProductHit } from "../../types/searchResults";
import { ProductResult, isProductResult } from "../../search-core/types/ProductResult";

/**
 * ProductCard - Displays a single product from search results
 *
 * Shows product image, name, price, and action button.
 * Uses minimal styling with flex layout.
 */
const ProductCard: React.FC<ProductCardProps> = ({ product, className = "", onActionClick, hasMore = false }) => {
	console.log("hasMore", hasMore);

	// Determine if product is ProductResult or ProductHit using type guards
	const isProdResult = isProductResult(product);
	const isProdHit = !isProdResult && isValidProductHit(product);

	// Extract data based on type
	let imageUrl, productName, productPrice, productUrl;
	let variantsCount: number | undefined;
	let available: boolean | undefined;
	let oldPrice: number | null | undefined;

	if (isProdResult) {
		const productResult = product as ProductResult;
		imageUrl = productResult.image || "/images/no-image.svg";
		productName = productResult.title || "Unnamed Product";
		productPrice = productResult.price || "Price not available";
		productUrl = productResult.url || "#";
		variantsCount = productResult.variantsCount;
		available = productResult.available;
		oldPrice = productResult.oldPrice;
	} else if (isProdHit) {
		const productHit = product as ProductHit;
		const { feed } = productHit;
		imageUrl = feed.picture || "/images/no-image.svg";
		productName = feed.name || "Unnamed Product";
		productPrice = feed.price || "Price not available";
		productUrl = feed.url || "#";
		available = feed.available;
		oldPrice = feed.oldPrice;
	} else {
		// Fallback for unknown type
		imageUrl = "/images/no-image.svg";
		productName = "Unnamed Product";
		productPrice = "Price not available";
		productUrl = "#";
	}
	const hasVariants = (variantsCount ?? 0) > 0;
	const handleActionClick = () => {
		if (onActionClick) {
			onActionClick(product);
		}
	};

	// Format price for display
	const formatPrice = (price: number | string): string => {
		if (typeof price === "number") {
			// Format number with spaces as thousands separators and add currency symbol
			return price.toLocaleString("ru-RU") + " ₽";
		}
		return String(price);
	};

	return (
		<div className={`product-card ${className}`}>
			<div className="product-image">
				<a href={productUrl} target="_blank" rel="noopener noreferrer" style={styles.imageLink}>
					<img
						src={imageUrl}
						alt={productName}
						onError={(e) => {
							// Fallback to placeholder on image error
							(e.target as HTMLImageElement).src = "/images/no-image.svg";
						}}
					/>
				</a>
				{hasVariants && (
					<div className="badge">
						<img src="/src/img/square-3-stack-3d.svg" alt="" />
						{!hasMore ? `+${variantsCount}` : `${variantsCount}+`}
					</div>
				)}
			</div>

			<div className="product-info">
				<a href={productUrl} className="product-name" target="_blank" rel="noopener noreferrer">
					{productName}
				</a>
				<div className="">
					<div className="flex justify-between items-center">
						<div className="flex items-center gap-2">
							<div className="product-price">{productPrice}</div>
							{oldPrice !== undefined && oldPrice !== null && <p className="old-price">{formatPrice(oldPrice)}</p>}
						</div>

						<button onClick={handleActionClick} className="product-action-button" disabled={available === false} aria-label={hasVariants ? "Просмотр товаров" : "Добавить в корзину"} title={hasVariants ? "Просмотр товаров" : "Добавить в корзину"}>
							<img src={hasVariants ? "/src/img/chevron-right.svg" : "/src/img/shopping-cart.svg"} alt="" />
						</button>
					</div>
					{available !== undefined && <p className={available ? "text-success-status" : "text-critical-status"}>{available ? "В наличии" : "Нет в наличии"}</p>}
				</div>
			</div>
		</div>
	);
};

// Minimal inline styles - will be moved to CSS later if needed
const styles = {
	card: {
		display: "flex",
		flexDirection: "row" as const,
		border: "1px solid #e2e8f0",
		borderRadius: "8px",
		overflow: "hidden",
		backgroundColor: "#ffffff",
		transition: "box-shadow 0.2s ease",
		height: "100%",
		minHeight: "280px",
	},
	imageContainer: {
		width: "100%",
		height: "150px",
		overflow: "hidden",
		backgroundColor: "#f8fafc",
		position: "relative" as const,
	},
	badge: {
		position: "absolute" as const,
		top: "8px",
		right: "8px",
		backgroundColor: "#2563eb",
		color: "#ffffff",
		fontSize: "12px",
		fontWeight: 600,
		padding: "4px 8px",
		borderRadius: "12px",
		lineHeight: 1,
		zIndex: 10,
		boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
	},
	imageLink: {
		display: "block",
		width: "100%",
		height: "100%",
	},
	image: {
		width: "100%",
		height: "100%",
		objectFit: "cover" as const,
		objectPosition: "center",
	},
	content: {
		flex: 1,
		padding: "12px",
		display: "flex",
		flexDirection: "column" as const,
	},
	name: {
		margin: "0 0 8px 0",
		fontSize: "14px",
		fontWeight: 500,
		lineHeight: "1.4",
		color: "#334155",
	},
	nameLink: {
		color: "inherit",
		textDecoration: "none",
	},
	price: {
		margin: "0 0 12px 0",
		fontSize: "16px",
		fontWeight: 600,
		color: "#2563eb",
	},
	actions: {
		marginTop: "auto",
		display: "flex",
		justifyContent: "flex-end",
	},
	actionButton: {
		padding: "8px 12px",
		backgroundColor: "#2563eb",
		color: "#ffffff",
		border: "none",
		borderRadius: "4px",
		cursor: "pointer",
		fontSize: "14px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		transition: "background-color 0.2s ease",
	},
	actionIcon: {
		fontSize: "14px",
	},
};

export default ProductCard;
